import React, { useEffect, useRef, useState } from 'react';
import styles from './Bottom.module.scss';
import BarcodeScanner from '../../shared/BarcodeScanner/BarcodeScanner';
import useComponentVisible from '../../../hooks/useComponentVisible';
import Logo from '../../shared/Logo/Logo';
import Modal from '../../shared/Modal/Modal';
import TicketResult from '../../shared/Modal/ModalContent/Ticket/Ticket';
import Navigation from './Navigation/Navigation';
import HeaderInput from './Input/Input';
import NumericKeyboard from '../../shared/VirtualKeyboard/NumericKeyboard';
import { getTranslation } from '../../../utils/translation';
import { useDispatch, useSelector } from 'react-redux';
import { resetErrorModalProps, setBarcodeScanned } from '../../../store/actions/global';
import BookBetService from '../../../services/BookBetService';
import { addCouponToBetSlip } from '../../../store/actions/betSlip';
import { showErrorNotification } from '../../../utils/render';
import FullKeyboard from '../../shared/VirtualKeyboard/FullKeyboard';
import { useHistory } from 'react-router-dom';
import {
    BARCODE_STARTING_CHARACTERS,
    VOUCHER_STAKE_AMOUNTS,
} from '../../../constants/defaultValues';
import {
    changeVoucherModalProps,
    createVoucher,
    resetVoucherModalProps,
} from '../../../store/actions/vouchers';
import { getUserAccount } from '../../../store/actions/user';
import VoucherDetailsModal from '../../shared/Modal/ModalContent/VoucherDetailsModal';
import { changeTicketModalProps, resetTicketModalProps } from '../../../store/actions/tickets';
import ErrorModal from '../../shared/Modal/ModalContent/ErrorModal/ErrorModal';

const initialInputProps = { value: '', showKeyboard: false };

export default function HeaderBottom() {
    const dispatch = useDispatch();
    const history = useHistory();

    const cashierInfo = useSelector(state => state.settings.account.data);
    const { isVisible: isTicketDetailsModalVisible } = useSelector(
        state => state.tickets.ticketModal,
    );
    const { isVisible: isVoucherDetailsModalVisible } = useSelector(
        state => state.vouchers.voucherModal,
    );
    const { isVisible: isErrorModalVisible } = useSelector(state => state.global.errorModal);
    const { isTicketSubmitting } = useSelector(state => state.betSlip);

    const [voucherInputProps, setVoucherInputProps] = useState(initialInputProps);
    const [barcodeInputProps, setBarcodeInputProps] = useState(initialInputProps);
    const [isVoucherSubmitting, setIsVoucherSubmitting] = useState(false);

    const ticketDetailsModalRef = useRef();
    const voucherDetailsModalRef = useRef();
    const errorModalRef = useRef();

    const {
        ref: voucherRef,
        isComponentVisible: isVoucherDropdownVisible,
        setIsComponentVisible: setIsVoucherDropdownVisible,
    } = useComponentVisible(false);

    const toggleVoucherKeyboardVisibility = () => {
        setVoucherInputProps(props => ({
            ...props,
            showKeyboard: !props.showKeyboard,
        }));
    };

    const closeVoucherKeyboard = () => {
        updateVoucherInputProps({ showKeyboard: false });
    };

    const toggleBarcodeKeyboardVisibility = () => {
        setBarcodeInputProps(props => ({
            ...props,
            showKeyboard: !props.showKeyboard,
        }));
    };

    const updateVoucherInputProps = newProps => {
        setVoucherInputProps(props => ({
            ...props,
            ...newProps,
        }));
    };

    const onVoucherInputClick = e => {
        if (!isVoucherDropdownVisible) {
            setIsVoucherDropdownVisible(true);
        }
    };

    const onVoucherAmountChange = value => {
        const parsedValue = parseInt(value);

        let formattedValue;

        if (value === '') {
            formattedValue = '';
        } else if (Number.isSafeInteger(parsedValue) && parsedValue > 0) {
            formattedValue = parsedValue;
        } else {
            formattedValue = voucherInputProps.value;
        }

        updateVoucherInputProps({ value: formattedValue });
    };

    const onVoucherSubmit = async () => {
        if (isVoucherSubmitting) return;

        closeVoucherKeyboard();
        setIsVoucherSubmitting(true);

        const info = await dispatch(createVoucher({ amount: voucherInputProps.value }));

        if (info.success) {
            dispatch(getUserAccount());
            updateVoucherInputProps({ value: '' });
            setIsVoucherDropdownVisible(false);
        }

        setIsVoucherSubmitting(false);
    };

    const updateBarcodeInputProps = (newProps, isScanned = false) => {
        if (isTicketSubmitting) return;

        setBarcodeInputProps(props => ({
            ...props,
            ...newProps,
        }));

        if (newProps.value) {
            onBarcodeSubmit({ value: newProps.value, isScanned });
        }
    };

    const onScanError = error => {
        console.error(error);
    };

    const onScan = data => {
        if (data === barcodeInputProps.value) return;

        dispatch(resetTicketModalProps());
        dispatch(resetVoucherModalProps());

        updateBarcodeInputProps({ value: data }, true);
    };

    const handleCouponScan = async value => {
        const { info, item } = await BookBetService.getCoupon({ couponNumber: value });

        if (!info.success) {
            showErrorNotification(getTranslation('generalErrorModalTitle'));
        } else {
            if (!item || !item.ticket) {
                showErrorNotification(getTranslation('couponNotFound'));
            } else {
                updateBarcodeInputProps({ value: '' });

                dispatch(
                    addCouponToBetSlip({
                        ...JSON.parse(item.ticket),
                        couponBarcode: item.ticketBarCode,
                        isMobile: item.isMobile,
                    }),
                );

                const { pathname } = history.location;

                if (!pathname.startsWith('/app/live') && !pathname.startsWith('/app/sports')) {
                    history.push('/app/sports');
                }
            }
        }
    };

    const handleVoucherScan = voucherCode => {
        dispatch(changeVoucherModalProps({ isVisible: true, voucherCode }));
    };

    const handleTicketScan = ticketCode => {
        dispatch(changeTicketModalProps({ isVisible: true, ticketCode }));
    };

    const onBarcodeSubmit = ({ value, isScanned }) => {
        const formattedValue = value.toLowerCase();

        if (formattedValue.startsWith(BARCODE_STARTING_CHARACTERS.coupon)) {
            if (formattedValue.length === 7) {
                handleCouponScan(formattedValue);
            }
        } else if (formattedValue.startsWith(BARCODE_STARTING_CHARACTERS.voucher)) {
            if (formattedValue.length === 14) {
                updateBarcodeInputProps(initialInputProps);
                dispatch(setBarcodeScanned(isScanned));
                handleVoucherScan(formattedValue.slice(1));
            }
        } else {
            if (formattedValue.length === 13) {
                updateBarcodeInputProps(initialInputProps);
                dispatch(setBarcodeScanned(isScanned));
                handleTicketScan(formattedValue);
            }
        }
    };

    const onModalClose = () => {
        dispatch(resetTicketModalProps());
        dispatch(resetVoucherModalProps());
        dispatch(resetErrorModalProps());
    };

    return (
        <>
            {isErrorModalVisible && (
                <Modal
                    ref={errorModalRef}
                    isVisible={isErrorModalVisible}
                    shouldCloseOnOutSideClick={true}
                    closeModal={onModalClose}
                    width='fit-content'
                    contentPadding='50px'
                    overflow={'auto'}
                >
                    <ErrorModal />
                </Modal>
            )}
            {isTicketDetailsModalVisible && (
                <Modal
                    ref={ticketDetailsModalRef}
                    isVisible={isTicketDetailsModalVisible}
                    shouldCloseOnOutSideClick={true}
                    closeModal={onModalClose}
                    width='fit-content'
                    minWidth={'900px'}
                    maxWidth={'90vw'}
                    contentPadding='50px'
                    overflow={'auto'}
                >
                    <TicketResult closeModal={onModalClose} />
                </Modal>
            )}
            {isVoucherDetailsModalVisible && (
                <Modal
                    ref={voucherDetailsModalRef}
                    shouldCloseOnOutSideClick={true}
                    closeModal={onModalClose}
                    isVisible={isVoucherDetailsModalVisible}
                    width='fit-content'
                    minWidth={'900px'}
                    maxWidth={'90vw'}
                    contentPadding='50px'
                    overflow={'auto'}
                >
                    <VoucherDetailsModal closeModal={onModalClose} />
                </Modal>
            )}
            {voucherInputProps.showKeyboard && (
                <NumericKeyboard
                    setInputValue={onVoucherAmountChange}
                    close={toggleVoucherKeyboardVisibility}
                    style={{ top: '120px', right: '535px' }}
                />
            )}
            {barcodeInputProps.showKeyboard && (
                <FullKeyboard
                    inputValue={barcodeInputProps.value}
                    setInputValue={value => updateBarcodeInputProps({ value })}
                    close={toggleBarcodeKeyboardVisibility}
                    style={{ top: '120px', right: '120px' }}
                />
            )}
            <div className={styles.headerBottom}>
                <div className={styles.logoArea}>
                    <Logo />
                </div>
                <div className={styles.navigationArea}>
                    <Navigation />
                </div>
                <div ref={voucherRef} className={styles.voucherInput}>
                    <HeaderInput
                        submitDisabled={!voucherInputProps.value || isVoucherSubmitting}
                        width='380px'
                        maxLength={14}
                        buttonText={getTranslation('common.actions.createVoucher')}
                        placeholder={getTranslation('input.placeholder.voucherInputPlaceholder')}
                        onSubmit={onVoucherSubmit}
                        value={voucherInputProps.value}
                        onChange={e => onVoucherAmountChange(e.target.value)}
                        onClick={onVoucherInputClick}
                        onFocus={e => e.target.select()}
                        showVirtualKeyboard={toggleVoucherKeyboardVisibility}
                    />
                    {isVoucherDropdownVisible && (
                        <div className={styles.voucherAmounts}>
                            {VOUCHER_STAKE_AMOUNTS.filter(
                                stake =>
                                    stake >= cashierInfo.minBettingStakeAmountForVoucher &&
                                    stake <= cashierInfo.maxBettingStakeAmountForVoucher &&
                                    stake.toString().includes(voucherInputProps.value),
                            ).map(amount => (
                                <div
                                    className={styles.voucherAmount}
                                    key={amount}
                                    onClick={() => {
                                        updateVoucherInputProps({ value: amount });
                                        setIsVoucherDropdownVisible(false);
                                    }}
                                >
                                    {amount}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className='input-area'>
                    <HeaderInput
                        submitDisabled={barcodeInputProps.value === ''}
                        inputDisabled={isTicketSubmitting}
                        width='380px'
                        maxLength={14}
                        buttonText={getTranslation('common.actions.checkIt')}
                        placeholder={getTranslation('input.placeholder.scanBarcode')}
                        onSubmit={() =>
                            onBarcodeSubmit({ value: barcodeInputProps.value, isScanned: false })
                        }
                        value={barcodeInputProps.value}
                        onChange={e => updateBarcodeInputProps({ value: e.target.value })}
                        onFocus={e => e.target.select()}
                        showVirtualKeyboard={toggleBarcodeKeyboardVisibility}
                    />
                    <BarcodeScanner onError={onScanError} onScan={onScan} />
                </div>
            </div>
        </>
    );
}
